<script setup>
/* global axios */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAccount } from 'dashboard/composables/useAccount';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const { accountId } = useAccount();

const STAGES = [
  { key: 'nuevo-contacto', label: 'Nuevo Contacto', color: '#76b658' },
  { key: 'seguimiento-efectivo', label: 'Seg. Efectivo', color: '#9dc353' },
  { key: 'seguimiento-no-efectivo', label: 'Seg. No Efectivo', color: '#f59e0b' },
  { key: 'agenda-posible', label: 'Agenda Posible', color: '#3b82f6' },
  { key: 'contacto-caido', label: 'Contacto Caido', color: '#ef4444' },
];

const PERIODS = [
  { label: 'Hoy', value: 'today' },
  { label: 'Semana', value: 'week' },
  { label: 'Mes', value: 'month' },
  { label: 'Todo', value: 'all' },
];

const CHANNEL_ICONS = {
  'Channel::Whatsapp': 'i-ri-whatsapp-line',
  'Channel::Instagram': 'i-ri-instagram-line',
  'Channel::FacebookPage': 'i-ri-facebook-circle-line',
  'Channel::Email': 'i-lucide-mail',
  'Channel::WebWidget': 'i-lucide-globe',
};

const isDark = ref(document.documentElement.classList.contains('dark'));
const observer = new MutationObserver(() => {
  isDark.value = document.documentElement.classList.contains('dark');
});

const counts = ref([0, 0, 0, 0, 0]);
const channelCounts = ref([]);
const loading = ref(true);
const loadingChannels = ref(true);
const loadingConversations = ref(false);
const lastUpdated = ref('');

const inboxes = ref([]);
const selectedPeriod = ref('all');
const selectedStage = ref(null);
const stageConversations = ref([]);

onMounted(async () => {
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  await fetchInboxes();
  await Promise.all([fetchCounts(), fetchChannelCounts()]);
});
onUnmounted(() => observer.disconnect());

watch(selectedPeriod, () => {
  if (selectedStage.value !== null) openStage(selectedStage.value);
});

const textColor = computed(() => isDark.value ? '#94a3b8' : '#64748b');
const gridColor = computed(() => isDark.value ? '#1e2535' : '#f1f5f9');
const maxChannel = computed(() => Math.max(...channelCounts.value.map(c => c.count), 1));

const barData = computed(() => ({
  labels: STAGES.map(s => s.label),
  datasets: [{
    data: counts.value,
    backgroundColor: STAGES.map(s => s.color + 'cc'),
    borderColor: STAGES.map(s => s.color),
    borderWidth: 1,
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const barOptions = computed(() => ({
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 500 },
  onClick: (_, elements) => {
    if (elements.length > 0) openStage(elements[0].index);
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ctx => ` ${ctx.raw} conversaciones`,
      },
    },
  },
  scales: {
    x: {
      grid: { color: gridColor.value },
      ticks: { color: textColor.value, font: { size: 11 }, stepSize: 1 },
      border: { display: false },
    },
    y: {
      grid: { display: false },
      ticks: { color: textColor.value, font: { size: 12 } },
      border: { display: false },
    },
  },
}));

async function fetchInboxes() {
  try {
    const res = await axios.get(`/api/v1/accounts/${accountId.value}/inboxes`);
    inboxes.value = res.data?.payload ?? [];
  } catch {
    inboxes.value = [];
  }
}

async function fetchCounts() {
  loading.value = true;
  const results = await Promise.all(
    STAGES.map(s =>
      ConversationApi.meta({ labels: s.key })
        .then(r => r.data?.meta?.all_count ?? 0)
        .catch(() => 0)
    )
  );
  counts.value = results;
  lastUpdated.value = new Date().toLocaleTimeString('es-AR');
  loading.value = false;
}

async function fetchChannelCounts() {
  loadingChannels.value = true;
  const results = await Promise.all(
    inboxes.value.map(inbox =>
      ConversationApi.meta({ inboxId: inbox.id })
        .then(r => ({
          id: inbox.id,
          name: inbox.name,
          channel: inbox.channel_type,
          count: r.data?.meta?.all_count ?? 0,
        }))
        .catch(() => ({ id: inbox.id, name: inbox.name, channel: inbox.channel_type, count: 0 }))
    )
  );
  channelCounts.value = results.sort((a, b) => b.count - a.count);
  loadingChannels.value = false;
}

function getDateFilter() {
  const now = new Date();
  if (selectedPeriod.value === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return { since: Math.floor(start / 1000) };
  }
  if (selectedPeriod.value === 'week') {
    const start = new Date(now); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { since: Math.floor(start / 1000) };
  }
  if (selectedPeriod.value === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { since: Math.floor(start / 1000) };
  }
  return {};
}

async function openStage(index) {
  selectedStage.value = index;
  stageConversations.value = [];
  loadingConversations.value = true;
  try {
    const res = await axios.get(`/api/v1/accounts/${accountId.value}/conversations`, {
      params: { labels: STAGES[index].key, page: 1, ...getDateFilter() },
    });
    stageConversations.value = res.data?.data?.payload ?? [];
  } catch {
    stageConversations.value = [];
  } finally {
    loadingConversations.value = false;
  }
}

function closeStage() {
  selectedStage.value = null;
  stageConversations.value = [];
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - ts * 1000) / 60000);
  if (diff < 60) return `hace ${diff}m`;
  if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
  return `hace ${Math.floor(diff / 1440)}d`;
}
</script>

<template>
  <div class="pt-6 pb-12">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Analiticas</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          Distribucion por etapa y canal — clic en barra para ver contactos
        </p>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="lastUpdated" class="text-xs text-n-slate-9">{{ lastUpdated }}</span>
        <button class="text-n-slate-11 hover:text-n-brand transition-colors" @click="fetchCounts(); fetchChannelCounts()">
          <span class="i-lucide-refresh-cw size-4" :class="{ 'animate-spin': loading }" />
        </button>
      </div>
    </div>

    <!-- Period filter -->
    <div class="flex items-center gap-2 mb-6">
      <button
        v-for="p in PERIODS"
        :key="p.value"
        class="text-sm px-4 py-1.5 rounded-lg border font-medium transition-all"
        :class="selectedPeriod === p.value
          ? 'bg-n-brand text-white border-n-brand'
          : 'border-n-weak text-n-slate-10 hover:border-n-brand hover:text-n-brand bg-n-surface-2'"
        @click="selectedPeriod = p.value"
      >
        {{ p.label }}
      </button>
    </div>

    <!-- Main grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

      <!-- Bar chart -->
      <div class="lg:col-span-2 rounded-xl border border-n-weak bg-n-surface-2 p-6">
        <h2 class="text-sm font-semibold text-n-slate-11 mb-1">Leads por etapa</h2>
        <p class="text-xs text-n-slate-9 mb-5">Clic en una barra para ver los contactos</p>

        <div v-if="loading" class="h-52 flex items-center justify-center">
          <span class="i-lucide-loader-2 size-8 text-n-slate-9 animate-spin" />
        </div>
        <div v-else class="h-52">
          <Bar :data="barData" :options="barOptions" />
        </div>
      </div>

      <!-- Channel breakdown -->
      <div class="rounded-xl border border-n-weak bg-n-surface-2 p-5">
        <h2 class="text-sm font-semibold text-n-slate-11 mb-4">Actividad por canal</h2>

        <div v-if="loadingChannels" class="space-y-3">
          <div v-for="i in 3" :key="i" class="h-10 bg-n-slate-3 rounded-lg animate-pulse" />
        </div>

        <div v-else-if="channelCounts.length === 0" class="py-6 text-center">
          <p class="text-xs text-n-slate-8">Sin canales configurados</p>
        </div>

        <div v-else class="space-y-3">
          <div v-for="ch in channelCounts" :key="ch.id">
            <div class="flex items-center gap-2 mb-1">
              <span :class="[CHANNEL_ICONS[ch.channel] ?? 'i-lucide-message-circle', 'size-4 text-n-slate-9 flex-shrink-0']" />
              <p class="text-sm text-n-slate-11 flex-1 truncate">{{ ch.name }}</p>
              <p class="text-sm font-semibold text-n-slate-12">{{ ch.count }}</p>
            </div>
            <div class="h-1.5 rounded-full bg-n-slate-4 overflow-hidden">
              <div
                class="h-full rounded-full bg-n-brand transition-all duration-700"
                :style="{ width: (ch.count / maxChannel * 100) + '%' }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drill-down panel -->
    <transition name="slide-down">
      <div
        v-if="selectedStage !== null"
        class="rounded-xl border border-n-weak bg-n-surface-2 overflow-hidden"
      >
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
          <div class="flex items-center gap-3">
            <span
              class="inline-block w-3 h-3 rounded-full"
              :style="{ backgroundColor: STAGES[selectedStage].color }"
            />
            <h3 class="font-semibold text-n-slate-12 text-sm">
              {{ STAGES[selectedStage].label }}
              <span class="text-n-slate-9 font-normal ml-1">
                — {{ PERIODS.find(p => p.value === selectedPeriod)?.label }}
              </span>
            </h3>
          </div>
          <button class="text-n-slate-9 hover:text-n-slate-12 transition-colors" @click="closeStage">
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div v-if="loadingConversations" class="flex items-center justify-center py-12">
          <span class="i-lucide-loader-2 size-6 text-n-slate-9 animate-spin" />
        </div>
        <div v-else-if="stageConversations.length === 0" class="py-10 text-center">
          <p class="text-sm text-n-slate-9">Sin conversaciones en este periodo</p>
        </div>
        <div v-else class="divide-y divide-n-weak max-h-80 overflow-y-auto">
          <div
            v-for="conv in stageConversations"
            :key="conv.id"
            class="flex items-center gap-4 px-5 py-3 hover:bg-n-surface-1 transition-colors"
          >
            <div class="w-8 h-8 rounded-full bg-n-brand/15 flex items-center justify-center flex-shrink-0">
              <span class="text-xs font-semibold text-n-brand">
                {{ (conv.meta?.sender?.name ?? '?')[0].toUpperCase() }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ conv.meta?.sender?.name ?? 'Sin nombre' }}
              </p>
              <p class="text-xs text-n-slate-9 truncate">
                {{ conv.last_non_activity_message?.content ?? 'Sin mensajes' }}
              </p>
            </div>
            <div class="flex-shrink-0 flex items-center gap-2">
              <span :class="[CHANNEL_ICONS[conv.channel] ?? 'i-lucide-message-circle', 'size-4 text-n-slate-9']" />
              <span class="text-xs text-n-slate-8">{{ timeAgo(conv.last_activity_at) }}</span>
            </div>
          </div>
        </div>
        <div v-if="stageConversations.length > 0" class="px-5 py-3 border-t border-n-weak">
          <p class="text-xs text-n-slate-8">Mostrando hasta 25 conversaciones.</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
